import api from "../../api";
import type {
  ContentDetailsActionType,
  ContentDetailsApiItem,
  ContentDetailsData,
  ContentDetailsEntityType,
  ContentProgressItem,
  PortalContentKind,
} from "../../../@types/portalAluno";
import { mapApiItemsToCards } from "../../../utils/portalAluno/mappers/contentCardMapper";
import { getContentDetailsConfig } from "../../../config/portalAluno/contentDetailsConfig";
import {
  hydrateEntityContents,
  hydrateTrailCourses,
} from "./contentDetailsHydration";
import {
  mapContentDetailsResponse,
  mapContentItem,
} from "../../../utils/portalAluno/mappers/contentDetailsMapper";

const feedbackEndpointByEntity: Record<ContentDetailsEntityType, string> = {
  comunicado: "mobile/comunicado/feedback",
  conteudo: "mobile/conteudo/feedback",
  curso: "mobile/curso/feedback",
  seguranca: "mobile/segurancaNoTrabalho/feedback",
  t1: "mobile/t1/feedback",
  trilha: "mobile/trilha/feedback",
};

const getRelatedCards = async (
  sourceType: PortalContentKind,
  entityType: ContentDetailsEntityType
) => {
  try {
    const response = await api.get<unknown>(
      feedbackEndpointByEntity[entityType]
    );

    return mapApiItemsToCards(response.data, sourceType);
  } catch {
    return [];
  }
};

export const getContentDetails = async (
  id: number,
  sourceType: PortalContentKind
): Promise<ContentDetailsData> => {
  const config = getContentDetailsConfig(sourceType);
  if (!config) throw new Error("Tipo de conteúdo inválido.");
  if (!Number.isInteger(id) || id <= 0) {
    throw new Error("Conteúdo inválido para carregar os detalhes.");
  }

  const [response, relatedCards] = await Promise.all([
    api.get<ContentDetailsApiItem>(config.endpoint, {
      params: { [config.paramKey]: id },
    }),
    getRelatedCards(sourceType, config.entityType),
  ]);

  const rawWithCourses =
    config.entityType === "trilha"
      ? await hydrateTrailCourses(response.data)
      : response.data;
  const raw = await hydrateEntityContents(rawWithCourses, config);

  const details = mapContentDetailsResponse(raw, sourceType, config);
  const seenIds = new Set<number>();

  return {
    ...details,
    relatedCards: [...details.relatedCards, ...relatedCards].filter((card) => {
      if (card.id === details.id || seenIds.has(card.id)) return false;
      seenIds.add(card.id);
      return true;
    }),
  };
};

export const getContentModule = async (
  idConteudo: number
): Promise<ContentProgressItem> => {
  if (!Number.isInteger(idConteudo) || idConteudo <= 0) {
    throw new Error("Módulo inválido para carregar o conteúdo.");
  }

  const response = await api.get<ContentDetailsApiItem>("mobile/conteudo", {
    params: { idConteudo },
  });
  const item = mapContentItem(response.data, "modulo");
  if (!item) throw new Error("A API retornou um módulo inválido.");

  return item;
};

export const toggleContentFavorite = async (
  id: number,
  type: ContentDetailsActionType
) => {
  await api.post("mobile/favorita", undefined, {
    params: { id, tipo: type },
  });
};

export const toggleContentLike = async (
  id: number,
  type: ContentDetailsActionType
) => {
  await api.post("mobile/curte", undefined, {
    params: { id, tipo: type },
  });
};

export const saveContentHistory = async (idConteudo: number) => {
  await api.post("mobile/salvarHistorico", undefined, {
    params: { idConteudo },
  });
};

export const submitContentFeedback = async (
  id: number,
  type: ContentDetailsEntityType,
  rating: number,
  description: string
) => {
  await api.post("mobile/feedback", {
    descricao: description,
    id,
    nota: rating,
    type,
  });
};
